import React, { useEffect } from 'react';
import styled from 'styled-components';
import assistantClient from '@requests/telnyx/assistants.requests';

import { v4 as uuidv4 } from 'uuid';
import { flowActions, showTelnyxFeatures, State, tabActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingButton } from '@common/Button';
import { Stack } from '@mui/material';
import { LayoutWorkspace } from '@common/layout';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { Table } from '@common/Table';
import { GridActionsCellItem, GridColDef, GridDeleteIcon, GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { AIAssistant } from '@requests/telnyx';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import { RequestError } from '@requests/common';
import { AssistantTabs } from './Assistant/AssistantTabs';
import { useTheme } from '../../../../styles';
import { DeleteAssitant } from '@components/Modals/Assistants/DeleteAssistant';
import { assistantActions } from '@store/slices/assistant';
import { getInferenceProviderLogo } from '@common/Autocomplete/Base';
import { Tooltip } from '@common/Tooltip';
import { AssistantImport } from './AssistantImport';

const LayoutContainer = styled.div`
  display: flex;
  height: calc(100vh - 61px);
  flex: 1;
`;

export const LayoutSidebar = styled.div<{ creatingAssistant: AIAssistant | null }>`
  display: grid;
  grid-template-columns: minmax(200px, 3fr) 7fr;
  flex: 1;

  & .MuiDataGrid-root {
    border-right: 1px solid ${({ theme }) => theme.line.light};
  }
`;

export const AssistantContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5rem;

  & .MuiChip-root {
    border-color: ${({ theme }) => theme.line.light};
  }
`;

export const AssistantContainerSection = styled.div`
  display: flex;
  flex-direction: column;

  padding: 1rem 2rem;
  padding-top: 1.1rem;
  gap: 1rem;
`;

export const Assistants: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { service_id, assistantId } = useParams();

  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);
  const createAssistant = useSelector((state: State) => state.assistant.createAssistant);
  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));

  const {
    data: assistants = [],
    isLoading,
    isFetching,
  } = useQuery<AIAssistant[], RequestError>({
    queryKey: ['ai_assistants'],
    queryFn: assistantClient.list,
    retry: false,
  });

  const assistant = assistants.find((assistant) => assistant.id === assistantId);

  useEffect(() => {
    if (!assistant && assistantId && !isLoading) navigate(`/${service_id}/assistants`);
  }, [assistant, isLoading]);

  const createNewAssistant = () => {
    try {
      const defaultAssistant: AIAssistant = {
        id: uuidv4(),
        name: '',
        description: '',
        model: '',
        instructions: '',
        created_at: new Date().toISOString(),
        tools: [],
      };

      dispatch(assistantActions.createNewAssistant(defaultAssistant));
    } catch {}
  };

  const removeAssistant = async (assistantId: string) => {
    try {
      dispatch(flowActions.setDeleteAssistant(assistantId));
    } catch {}
  };

  const columns: GridColDef<AIAssistant>[] = [
    {
      field: 'name',
      headerName: 'Assistant',
      flex: 100,
      sortable: true,
      renderCell: (params: GridRenderCellParams<AIAssistant>) => {
        const { row } = params;

        return (
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <AutoAwesomeOutlined sx={{ color: theme.text.subtext, fontSize: '1.8ch !important' }} />
            <div>{row.name}</div>
          </Stack>
        );
      },
    },
    {
      field: 'model',
      headerName: 'Model',
      flex: 25,
      sortable: true,
      renderCell: (params: GridRenderCellParams<AIAssistant>) => {
        const provider = params.value.split('/')[0];
        const providerLogo = getInferenceProviderLogo(provider, theme.text.standard);

        return (
          <Tooltip title={params.value} placement='left' arrow>
            <Stack display='flex' alignItems='center' justifyContent='center' height='30px' width='30px'>
              {providerLogo}
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            key='delete'
            icon={<GridDeleteIcon />}
            label='Delete'
            color='inherit'
            onClick={() => removeAssistant(id.toString())}
          />,
        ];
      },
    },
  ];

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={
        <LayoutContainer>
          <ServiceNavigation />
          <LayoutSidebar creatingAssistant={createAssistant}>
            <Table
              height='calc(100vh - 61px)'
              data={assistants}
              noAutoWidth
              disabled={Boolean(createAssistant)}
              toolbarSearchOnly
              selectionModel={assistantId ? [assistantId] : []}
              columns={columns}
              density={'standard'}
              sortModel='name'
              getRowId={(row) => row.id}
              loading={isFetching}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'name', sort: 'asc' }],
                },
              }}
              onRowClick={({ row }) => {
                if (row.id === assistantId) {
                  const path = `/${service_id}/assistants`;
                  dispatch(tabActions.setLastMenu(path));
                  navigate(path);
                } else {
                  const path = `/${service_id}/assistants/${row.id}`;
                  dispatch(tabActions.setLastMenu(path));
                  navigate(path);
                }
              }}
            />
            <AssistantTabs key={assistantId} isAssistantsLoading={isFetching} assistant={assistant} />
          </LayoutSidebar>
          <DeleteAssitant />
        </LayoutContainer>
      }
      actions={
        <Stack direction='row' spacing={1.5}>
          {isTelnyxUser && <AssistantImport disabled={Boolean(createAssistant)} />}
          <LoadingButton
            customColor='black'
            startIcon={<AutoAwesomeOutlined fontSize='small' />}
            onClick={createNewAssistant}
            disabled={Boolean(createAssistant)}
            minWidth='174px'
            minHeight='32px'
          >
            Create Assistant
          </LoadingButton>
        </Stack>
      }
    />
  );
};
